import { FC, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { clsx } from "clsx";
import {
    DataTable,
    DataTableColumn,
    DataTableEntityCell,
    Drawer,
    Spin,
    StatusDot,
} from "../../design-system";
import { SvgIcon } from "../../common/components/SvgIcon";
import { handleError } from "../../common/ErrorHandling";
import {
    listNotifications,
    markNotificationRead,
    markNotificationsRead,
} from "../../common/api/NotificationsClient";
import { getRelativeTime } from "../date/DateOp";
import { projectLogoFallback } from "../project/components/VendorLogo";
import { UserNotification } from "../../types/NotificationTypes";
import { useAppShellContext } from "../layout/AppShell";
import classes from "./NotificationsPanel.module.less";

const PAGE_SIZE = 50;
// As wide as the picture, less when the stage is narrower than that plus
// the gaps on both sides.
const WIDTH = "min(880px, calc(100% - 64px))";

export interface NotificationsPanelProps {
    open: boolean;
    onClose: () => void;
    /** How many are unread, from the nav's polling; shows the mark-all button. */
    unreadCount: number;
    /** The user opened this notification; it is read now. */
    onRead: (id: number) => void;
    /** The user marked every notification read. */
    onReadAll: () => void;
}

/**
 * The notifications panel (plans/notifications.md, section 5): every
 * notification the user received, newest first, the older ones loading as
 * the list is scrolled down to them, back to the first. A dark panel over
 * the stage, opened from the nav, so the page behind it stays. A
 * notification is read when the user opens it, or all of them at once
 * from the header; looking at the list is not reading.
 */
export const NotificationsPanel: FC<NotificationsPanelProps> = ({
    open,
    onClose,
    unreadCount,
    onRead,
    onReadAll,
}) => {
    const { getOverlayContainer } = useAppShellContext();
    const [rows, setRows] = useState<UserNotification[]>([]);
    // The continuation token: the last id shown, null once everything is.
    const [next, setNext] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const bodyRef = useRef<HTMLDivElement>(null);
    // Sits under the last row; scrolling it into view asks for the next page.
    const sentinelRef = useRef<HTMLDivElement>(null);

    const load = async (before: number | null) => {
        setLoading(true);
        try {
            const page = await listNotifications(PAGE_SIZE, before);
            setRows((previous) => (before == null ? page.data : [...previous, ...page.data]));
            setNext(page.next);
        } catch (error: any) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    // Fresh on every opening: what happened since last time is the point.
    useEffect(() => {
        if (!open) return;
        bodyRef.current?.scrollTo({ top: 0 });
        void load(null);
    }, [open]);

    const readNow = new Date().toISOString();

    // Opening a notification reads it. The row changes at once; the server
    // follows, and the nav is told so its count keeps up.
    const markRead = async (notification: UserNotification) => {
        if (notification.readDate != null) return;
        setRows((previous) =>
            previous.map((row) =>
                row.id === notification.id ? { ...row, readDate: readNow } : row
            )
        );
        try {
            await markNotificationRead(notification.id);
            onRead(notification.id);
        } catch (error: any) {
            handleError(error);
        }
    };

    const markAllRead = async () => {
        setRows((previous) =>
            previous.map((row) => (row.readDate == null ? { ...row, readDate: readNow } : row))
        );
        try {
            await markNotificationsRead();
            onReadAll();
        } catch (error: any) {
            handleError(error);
        }
    };

    // The next page loads once the sentinel scrolls into view, and again
    // right away when a page is too short to push it out of view, so the
    // list fills the panel on its own.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!open || loading || next == null || !sentinel) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) void load(next);
            },
            { root: bodyRef.current }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [open, loading, next]);

    const columns: DataTableColumn<UserNotification>[] = [
        {
            key: "event",
            title: "Notification",
            render: (notification) => {
                const { projectId, projectName, vendorName, vendorHero } = notification.data;
                const logo = projectLogoFallback(vendorName, projectName);
                return (
                    <DataTableEntityCell
                        imageSrc={vendorHero || undefined}
                        imageFallback={logo.letter}
                        imageFallbackColor={logo.color}
                        name={
                            <Link
                                to={`/project/${projectId}`}
                                onClick={() => {
                                    void markRead(notification);
                                    onClose();
                                }}
                            >
                                {projectName || "Unnamed project"}
                            </Link>
                        }
                        detail={
                            <div
                                className={clsx(
                                    classes.text,
                                    notification.readDate != null && classes["text--read"]
                                )}
                            >
                                {notification.text}
                            </div>
                        }
                    />
                );
            },
        },
        {
            key: "when",
            title: "When",
            width: "160px",
            align: "right",
            // The only other column: nothing to hide, so no columns menu.
            alwaysVisible: true,
            // An orange dot marks the ones not read yet.
            render: (notification) => {
                const date = new Date(notification.recordCreationDate);
                const when = (
                    <span className={classes.when} title={moment(date).format("LLL")}>
                        {getRelativeTime(date)}
                    </span>
                );
                return notification.readDate == null ? (
                    <StatusDot color="orange">{when}</StatusDot>
                ) : (
                    when
                );
            },
        },
    ];

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="left"
            width={WIDTH}
            closable={false}
            // Over the stage, so the mask dims the page and not the nav the
            // panel opened from, and in its frame rather than the stage
            // itself, so it stays put while the page scrolls. The frame is
            // mounted before the panel can open; the fallback only satisfies
            // the type.
            getContainer={() => getOverlayContainer() ?? document.body}
            style={{ position: "absolute" }}
            // antd 4 puts className on the drawer's root (and drops rootClassName).
            className={classes.root}
            bodyStyle={{ padding: 0 }}
        >
            <div className={classes.panel}>
                <div className={classes.header}>
                    {/* The count is the unread one, so "new" says which; at zero
                        the sentence stays a sentence rather than showing a 0. */}
                    <h2 className={classes.title}>
                        {unreadCount > 0 ? (
                            <>
                                You have <span className={classes.count}>{unreadCount}</span> new{" "}
                                {unreadCount === 1 ? "notification" : "notifications"}
                            </>
                        ) : (
                            "You have no new notifications"
                        )}
                    </h2>
                    {unreadCount > 0 && (
                        <button type="button" className={classes.action} onClick={markAllRead}>
                            Mark all as read
                        </button>
                    )}
                    <button
                        type="button"
                        className={classes.close}
                        aria-label="Close"
                        onClick={onClose}
                    >
                        <SvgIcon name="close" size={16} aria-hidden="true" />
                    </button>
                </div>
                <div className={classes.body} ref={bodyRef}>
                    <DataTable<UserNotification>
                        columns={columns}
                        rows={rows}
                        rowKey={(notification) => notification.id}
                        sortable={false}
                        hideHeader
                        dark
                        emptyText={loading ? "Loading" : "Nothing has happened yet."}
                    />
                    {next != null ? (
                        <div className={classes.more} ref={sentinelRef}>
                            {loading && <Spin size="small" />}
                        </div>
                    ) : (
                        rows.length > 0 && (
                            <div className={classes.end}>You have seen them all.</div>
                        )
                    )}
                </div>
            </div>
        </Drawer>
    );
};
